import { motion } from "framer-motion";
import { CheckCircle, Award, Shield, Star, Users } from "lucide-react";
import BackgroundPattern from "@/components/BackgroundPattern";

const credentials = [
  { icon: Award, text: "Owens Corning Preferred Contractor — top 1% of roofers nationwide" },
  { icon: Shield, text: "Fully licensed (NM CID), bonded, and insured" },
  { icon: Star, text: "A+ BBB rating with 150+ five-star Google reviews" },
  { icon: Users, text: "1,000+ completed projects across Central New Mexico" },
  { icon: CheckCircle, text: "Lifetime manufacturer warranties on qualifying systems" },
];

const AboutZenithSection = () => (
  <section
    id="about-zenith"
    className="section-padding bg-section-light relative"
    aria-label="About Zenith Solutions roofing company"
  >
    <BackgroundPattern variant="grid" />
    <div className="container mx-auto max-w-4xl relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="text-center mb-10"
      >
        <p className="text-sm font-bold uppercase tracking-widest text-primary mb-3">About Zenith Solutions</p>
        <h2 className="text-3xl md:text-4xl font-black text-foreground mb-6">
          Why Albuquerque Homeowners Choose Zenith Solutions
        </h2>
      </motion.div>

      <motion.article
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="prose prose-lg max-w-none text-muted-foreground leading-relaxed space-y-4"
        itemScope
        itemType="https://schema.org/Article"
      >
        <p>
          <strong className="text-foreground">Zenith Solutions</strong> is Albuquerque, New Mexico's highest-rated roofing
          contractor and full-service home improvement company. Founded on the principle of{" "}
          <em>"educate first, deliver excellence second,"</em> Zenith has completed over{" "}
          <strong className="text-foreground">1,000 projects</strong> for homeowners across Central New Mexico — earning a
          perfect <strong className="text-foreground">5.0-star rating</strong> from 150+ verified Google reviews and an{" "}
          <strong className="text-foreground">A+ Better Business Bureau rating</strong>.
        </p>

        <p>
          As an <strong className="text-foreground">Owens Corning Preferred Contractor</strong> — a distinction held by
          only the top 1% of roofing professionals in the United States — Zenith Solutions offers enhanced manufacturer
          warranties, including the Platinum Protection limited lifetime warranty, that most contractors simply cannot
          provide. This certification ensures every roof installation meets the highest standards for materials,
          workmanship, and long-term durability.
        </p>

        <p>
          Zenith specializes in <strong className="text-foreground">residential and commercial roof replacement</strong>,
          storm and hail damage repair, insurance claim navigation, solar panel installation, general construction and
          remodeling, and professional mold remediation. The company serves Albuquerque, Rio Rancho, Corrales, Santa Fe,
          Los Lunas, Bernalillo, Edgewood, and 15+ additional communities throughout New Mexico.
        </p>

        <p>
          What sets Zenith apart is their transparent, no-pressure approach. Every project begins with a{" "}
          <strong className="text-foreground">free inspection and detailed photo report</strong>, followed by a clear
          proposal with line-item pricing, material options, and financing if needed. Their certified crews typically
          complete roof replacements in 1–3 days, and every project includes a final walkthrough and warranty registration.
        </p>
      </motion.article>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="mt-10 grid sm:grid-cols-2 gap-3"
      >
        {credentials.map((c) => (
          <div key={c.text} className="flex items-start gap-3 bg-card border rounded-xl p-4">
            <c.icon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span className="text-sm font-medium text-foreground">{c.text}</span>
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);

export default AboutZenithSection;
